import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AndamentoCorreiosResponse } from './models/andamento-correios.model';
import {
  ControleToiFiltro,
  ControleToiListaResponse,
  ControleToiListItem,
} from './models/controle-toi.model';
import { ArquivoRetCorreios, ListaArquivosRetOutput } from './models/arquivo-ret.model';
import { PdfToiResponse } from './models/pdf-toi.model';
import {
  AtividadeToiPorAtividadeOutput,
  ControleToiListaApiResponse,
  CorrecaoEnderecoToiPayload,
  MxlAtividadeToiStatusOutput,
  ToiDetalheUpdatePayload,
  ToiSalvarResponse,
} from './models/toi-api.model';
import { ToiDetalhe } from './models/toi-detalhe.model';
import { ToiPdfUploadResponse } from './models/toi-pdf-upload.model';
import {
  MxlAtividadeToiLoteItemOutput,
  MxlAtividadeToiLoteItemsOutput,
  MxlAtividadeToiLoteListaOutput,
  MxlAtividadeToiLoteStatusOutput,
  TratamentoLote,
  TratamentoLoteAcaoResponse,
  TratamentoLoteFiltro,
  TratamentoLoteItem,
  TratamentoLoteListaResponse,
} from './models/tratamento-lote.model';
import { mapRastreioApiResponse } from './utils/andamento-correios.util';
import {
  extrairItensControleToi,
  extrairTotalRegistrosControleToi,
  mapControleToiListItem,
} from './utils/controle-toi.util';
import { mapAtividadeToiToPdf } from './utils/pdf-toi.util';
import { formatarDataParamToi, garantirRetornoToiOk } from './utils/toi-api.util';
import { mapAtividadeToiToDetalhe } from './utils/toi-detalhe.util';
import { mapLoteItemsResponse, mapLoteOutput } from './utils/tratamento-lote.util';

@Injectable({ providedIn: 'root' })
export class ToiService {
  private readonly baseUrl = `${environment.ApiCore}v3/MxlAtividadeToi`;
  /** Endpoints eCarta (envio/confirmação de lote) rodam em um serviço separado. */
  private static readonly API_TOI_CORREIO =
    'https://copasa-toi-correio-h2ejekgke6axbagk.brazilsouth-01.azurewebsites.net/api/';
  private readonly correioBaseUrl = `${ToiService.API_TOI_CORREIO}v3/MxlAtividadeToi`;

  private static readonly PDF_UPLOAD_ENDPOINT = 'SalvarPdf';
  private static readonly PDF_UPLOAD_FIELD_FILE = 'arquivo';

  /** Cache da lista de status TOI (não muda durante a sessão). */
  private statusToi$?: Observable<MxlAtividadeToiStatusOutput[]>;

  /** Cache da lista de status de lote (não muda durante a sessão). */
  private statusLote$?: Observable<MxlAtividadeToiLoteStatusOutput[]>;

  constructor(private http: HttpClient) {}

  /** GET v3/MxlAtividadeToi/Status — status TOI ativos do ambiente, na ordem do fluxo. */
  getStatusToi(): Observable<MxlAtividadeToiStatusOutput[]> {
    if (!this.statusToi$) {
      this.statusToi$ = this.http.get<MxlAtividadeToiStatusOutput[]>(`${this.baseUrl}/Status`).pipe(
        catchError((error) => {
          // Não mantém em cache uma falha: a próxima chamada tenta de novo.
          this.statusToi$ = undefined;
          return throwError(() => error);
        }),
        shareReplay(1)
      );
    }

    return this.statusToi$;
  }

  /** GET v3/MxlAtividadeToi/LoteStatus — status de lote cadastrados no ambiente. */
  getStatusLote(): Observable<MxlAtividadeToiLoteStatusOutput[]> {
    if (!this.statusLote$) {
      this.statusLote$ = this.http
        .get<MxlAtividadeToiLoteStatusOutput[]>(`${this.baseUrl}/LoteStatus`)
        .pipe(
          catchError((error) => {
            this.statusLote$ = undefined;
            return throwError(() => error);
          }),
          shareReplay(1)
        );
    }

    return this.statusLote$;
  }

  /** Detalhe e PDF do TOI em uma única chamada — não separar, para não duplicar a requisição. */
  getAtividadeToiData(
    idAtividade: number
  ): Observable<{ pdfToi: PdfToiResponse; detalhe: ToiDetalhe }> {
    return this.getPorAtividade(idAtividade).pipe(
      map((response) => ({
        pdfToi: mapAtividadeToiToPdf(response),
        detalhe: mapAtividadeToiToDetalhe(response),
      }))
    );
  }

  /** GET v3/MxlAtividadeToi/PorAtividade/{idAtividade} */
  private getPorAtividade(idAtividade: number): Observable<AtividadeToiPorAtividadeOutput> {
    return this.http.get<AtividadeToiPorAtividadeOutput>(`${this.baseUrl}/PorAtividade/${idAtividade}`);
  }

  /** GET v3/MxlAtividadeToi/Lista */
  getControleList(
    filtro: ControleToiFiltro,
    page = 0,
    pageSize = 50
  ): Observable<ControleToiListaResponse> {
    return this.http.get<ControleToiListaApiResponse>(`${this.baseUrl}/Lista`, {
        params: this.buildControleListParams(filtro, page, pageSize),
      }).pipe(map((response) => {
          const itens = extrairItensControleToi(response).map(mapControleToiListItem);

          return {
            itens,
            totalRegistros: extrairTotalRegistrosControleToi(response, itens.length),
          };
        })
      );
  }

  /** Busca todos os registros do controle TOI conforme filtros (paginação automática). */
  getControleListAll(filtro: ControleToiFiltro): Observable<ControleToiListItem[]> {
    const pageSize = 500;

    return this.getControleList(filtro, 0, pageSize).pipe(
      switchMap((first) => {
        const total = first.totalRegistros ?? first.itens.length;
        if (total <= first.itens.length) {
          return of(first.itens);
        }

        const totalPages = Math.ceil(total / pageSize);
        if (totalPages <= 1) {
          return of(first.itens);
        }

        const outrasPaginas = Array.from({ length: totalPages - 1 }, (_, index) => index + 1);
        return forkJoin(
          outrasPaginas.map((page) => this.getControleList(filtro, page, pageSize))
        ).pipe(
          map((responses) => [
            ...first.itens,
            ...responses.flatMap((response) => response.itens),
          ])
        );
      })
    );
  }

  /** Resolve idAtividade a partir do número da OS (Controle TOI /Lista). */
  resolverIdAtividadePorNumeroBa(numeroBa: string): Observable<number | null> {
    const termo = numeroBa.trim();
    if (!termo) {
      return of(null);
    }

    return this.getControleList({ numeroBa: termo }, 0, 1).pipe(map((response) => response.itens?.[0]?.idAtividade ?? null)
    );
  }

  /**
   * GET v3/MxlAtividadeToi/GetListaLotes
   * Prioridade de filtros: numeroBa > numeroLote > período de envio. Estrutura e status
   * são aplicados sempre. Sem período, o backend retorna os lotes em aberto + enviados hoje.
   */
  getTratamentoLotes(filtro: TratamentoLoteFiltro = {}): Observable<TratamentoLoteListaResponse> {
    return this.http.get<MxlAtividadeToiLoteListaOutput>(`${this.baseUrl}/GetListaLotes`, {
        params: this.buildTratamentoLoteParams(filtro),
      }).pipe(map((response) => ({
          lotes: (response?.itens ?? []).map(mapLoteOutput),
          indicadores: response?.indicadoresLote ?? null,
          totalRegistros: response?.totalRegistros ?? response?.itens?.length ?? 0,
          truncado: response?.truncado ?? false,
        }))
      );
  }

  /** GET v3/MxlAtividadeToi/GetListaLoteItems/{idAtividadeToiLote} */
  getLoteItems(idAtividadeToiLote: number): Observable<TratamentoLoteItem[]> {
    return this.http
      .get<MxlAtividadeToiLoteItemsOutput | MxlAtividadeToiLoteItemOutput[]>(
        `${this.baseUrl}/GetListaLoteItems/${idAtividadeToiLote}`
      )
      .pipe(map(mapLoteItemsResponse));
  }

  /** POST v3/MxlAtividadeToi/ConfirmarLoteEcarta/{idAtividadeToiLote} (serviço copasa-toi-correio) — envio definitivo do lote. */
  confirmarLote(idAtividadeToiLote: number): Observable<TratamentoLoteAcaoResponse> {
    return this.http
      .post<TratamentoLoteAcaoResponse>(
        `${this.correioBaseUrl}/ConfirmarLoteEcarta/${idAtividadeToiLote}`,
        null
      )
      .pipe(
        map((res) => garantirRetornoToiOk(res, 'Falha ao confirmar o envio definitivo do lote.'))
      );
  }

  /** POST v3/MxlAtividadeToi/EnviarLoteEcarta/{idAtividadeToiLote} (serviço copasa-toi-correio) — envio do lote. */
  enviarLote(idAtividadeToiLote: number): Observable<TratamentoLoteAcaoResponse> {
    return this.http
      .post<TratamentoLoteAcaoResponse>(
        `${this.correioBaseUrl}/EnviarLoteEcarta/${idAtividadeToiLote}`,
        null
      )
      .pipe(map((res) => garantirRetornoToiOk(res, 'Falha ao enviar o lote.')));
  }

  /** GET v3/MxlAtividadeToi/Rastreio/{idAtividadeToi} — rastreio de um TOI específico. */
  getRastreioCorreios(idAtividadeToi: number): Observable<AndamentoCorreiosResponse> {
    return this.http
      .get<unknown>(`${this.baseUrl}/Rastreio/${idAtividadeToi}`)
      .pipe(map((response) => mapRastreioApiResponse(response)));
  }

  /** GET v3/MxlAtividadeToi/RastreioLote/{idAtividadeToiLote} — rastreio de todos os itens do lote. */
  getRastreioLoteCorreios(idAtividadeToiLote: number): Observable<AndamentoCorreiosResponse> {
    return this.http.get<unknown>(`${this.baseUrl}/RastreioLote/${idAtividadeToiLote}`).pipe(
      map((response) =>
        mapRastreioApiResponse(response, { idAtividadeToiLote, idLote: idAtividadeToiLote })
      )
    );
  }

  getAndamentoCorreios(lote: TratamentoLote): Observable<AndamentoCorreiosResponse> {
    return this.getRastreioLoteCorreios(lote.idAtividadeToiLote).pipe(
      map((response) => ({
        ...response,
        idLote: response.idLote || lote.idLote,
        nrLoteCorreios: response.nrLoteCorreios || lote.nrLoteCorreios,
      }))
    );
  }

  sincronizarAndamentoCorreios(
  lote: TratamentoLote
): Observable<AndamentoCorreiosResponse> {
  return this.http
    .post<unknown>(
      `${this.baseUrl}/SincronizarRastreioLote/${lote.idAtividadeToiLote}`,
      {}
    )
    .pipe(
      switchMap(() => this.getAndamentoCorreios(lote))
    );
}

  /** GET v3/MxlAtividadeToi/ListaArquivosRET — arquivos RET dos Correios gerados pelo backend, para download. */
  getArquivosRet(): Observable<ArquivoRetCorreios[]> {
    return this.http
      .get<ListaArquivosRetOutput | null>(`${this.baseUrl}/ListaArquivosRET`)
      .pipe(
        map((response) => {
          const resposta = garantirRetornoToiOk(
            response ?? {},
            'Não foi possível carregar os arquivos RET dos Correios.'
          );
          return resposta.arquivos ?? [];
        })
      );
  }

  /** PUT v3/MxlAtividadeToi */
  salvarDetalhe(payload: ToiDetalheUpdatePayload): Observable<ToiSalvarResponse> {
    return this.http
      .put<ToiSalvarResponse>(`${this.baseUrl}`, payload)
      .pipe(map((res) => garantirRetornoToiOk(res)));
  }

  /** PUT v3/MxlAtividadeToi/CorrecaoEnderecoToi/{idAtividadeToi} */
  correcaoEnderecoToi(
    idAtividadeToi: number,
    payload: CorrecaoEnderecoToiPayload
  ): Observable<ToiSalvarResponse> {
    return this.http.put<ToiSalvarResponse>(`${this.baseUrl}/CorrecaoEnderecoToi/${idAtividadeToi}`, payload).pipe(map((res) => garantirRetornoToiOk(res)));
  }

  /** POST v3/MxlAtividadeToi/ValidarToi/{idAtividadeToi}/{validacao} */
  validarToi(idAtividadeToi: number, validacao: boolean): Observable<ToiSalvarResponse> {
    return this.http.post<ToiSalvarResponse>(`${this.baseUrl}/ValidarToi/${idAtividadeToi}/${validacao}`, null).pipe(map((res) => garantirRetornoToiOk(res)));
  }

  /** POST api/v3/MxlAtividadeToi/SalvarPdf/{idAtividadeToi} — multipart com o arquivo PDF. */
  enviarPdfToi(
    idAtividadeToi: number,
    arquivo: Blob,
    nomeArquivo: string
  ): Observable<ToiPdfUploadResponse> {
    const formData = new FormData();
    formData.append(ToiService.PDF_UPLOAD_FIELD_FILE, arquivo, nomeArquivo);

    return this.http.post<ToiPdfUploadResponse>(
        `${this.baseUrl}/${ToiService.PDF_UPLOAD_ENDPOINT}/${idAtividadeToi}`,formData).pipe(map((res) => garantirRetornoToiOk(res, 'Falha ao enviar PDF do TOI.')));
  }

  /** Alguns filtros são repetidos em aliases distintos por compatibilidade do backend. */
  private buildControleListParams(
    filtro: ControleToiFiltro,
    page: number,
    pageSize: number
  ): HttpParams {
    let params = new HttpParams().set('page', (page < 0 ? 0 : page).toString()).set('pageSize', pageSize.toString());

    if (filtro.idHierarquia && filtro.idHierarquia > 0) {
      params = params.set('idHierarquia', filtro.idHierarquia.toString());
    }
    if (filtro.dataInicio) {
      const dataInicio = formatarDataParamToi(filtro.dataInicio);
      params = params.set('dataInicio', dataInicio).set('dataIni', dataInicio);
    }
    if (filtro.dataFim) {
      params = params.set('dataFim', formatarDataParamToi(filtro.dataFim));
    }
    if (filtro.cdServico?.length) {
      params = params.set('cdServico', filtro.cdServico.join(','));
    }
    // Um parâmetro por tipo (a descrição pode conter vírgula, então não usa join).
    filtro.dsTipoInfracao?.forEach((dsTipoInfracao) => {
      params = params.append('dsTipoInfracao', dsTipoInfracao);
    });
    if (filtro.idAtividadeStatusToi && filtro.idAtividadeStatusToi > 0) {
      params = params.set('idAtividadeStatusToi', filtro.idAtividadeStatusToi.toString());
    }
    if (filtro.numeroBa?.trim()) {
      const numeroBa = filtro.numeroBa.trim();
        params = params.set('numeroBa', numeroBa)
        .set('numeroBa', numeroBa)
        .set('numeroBaOriginal', numeroBa)
        .set('numBAOriginal', numeroBa);
    }

    return params;
  }

  private buildTratamentoLoteParams(filtro: TratamentoLoteFiltro): HttpParams {
    let params = new HttpParams();

    if (filtro.idHierarquia && filtro.idHierarquia > 0) {
      params = params.set('idHierarquia', filtro.idHierarquia.toString());
    }
    if (filtro.numeroBa?.trim()) {
      params = params.set('numeroBa', filtro.numeroBa.trim());
    }
    if (filtro.numeroLote?.trim()) {
      params = params.set('numeroLote', filtro.numeroLote.trim());
    }
    if (filtro.nmStatus?.trim()) {
      params = params.set('nmStatus', filtro.nmStatus.trim());
    }
    if (filtro.dataEnvioIni) {
      params = params.set('dataEnvioIni', formatarDataParamToi(filtro.dataEnvioIni));
    }
    if (filtro.dataEnvioFim) {
      params = params.set('dataEnvioFim', formatarDataParamToi(filtro.dataEnvioFim));
    }

    return params;
  }
}
